import {
  CROSS_CLIENT_MATCH_NONE,
  CrossClientAction,
  CrossClientQueryEvaluation,
  CrossClientQueryEvaluationResultDetail,
  CrossClientQueryEvaluationStatus,
  CrossClientQueryEvaluationType,
  CrossClientQueryExecution,
  CrossClientQueryExecutionProgress,
  CrossClientQueryExecutionProgressResponse,
  CrossClientQueryPhase,
  CrossClientQueryResolverStage,
  CrossClientQueryResult,
  CrossClientQueryResultBucketCount,
  CrossClientQueryResultMatch,
  CrossClientQueryResultMatches,
  CrossClientQueryResultOrderBy,
  CrossClientQueryResultStatus,
  PredefinedCrossClientQuery,
} from '../../models/CrossClient'
import { type LocationRef } from '../../models/Scheduling'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { paginate } from '../utils'
import { createApi } from '@reduxjs/toolkit/query/react'

const _AI_MANAGEMENT_BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

type ExecutionExpand = ('matches' | 'progress' | 'evaluationCounts')[]

type ResultMatchFilter = (CrossClientQueryResultMatch | typeof CROSS_CLIENT_MATCH_NONE)[]

interface GetCrossClientQueryExecutionResultsRequest extends BaseRequest {
  executionId: string
  updatedAfter?: string | null
  match?: ResultMatchFilter
  status?: CrossClientQueryResultStatus
  lastResolverStage?: CrossClientQueryResolverStage[]
  clientIds?: string[]
  resultIds?: string[]
  search?: string
  hasActions?: boolean
  orderBy?: CrossClientQueryResultOrderBy
}

interface GetCrossClientQueryExecutionResultsPageRequest extends BaseRequest {
  executionId: string
  match?: ResultMatchFilter
  status?: CrossClientQueryResultStatus
  lastResolverStage?: CrossClientQueryResolverStage[]
  clientIds?: string[]
  resultIds?: string[]
  search?: string
  hasActions?: boolean
  orderBy?: CrossClientQueryResultOrderBy
  include?: ('bulkResolverAnswers' | 'localResolverAnswers' | 'scores')[]
  page: number
  size: number
}

interface GetAllCrossClientQueryExecutionsRequest extends BaseRequest {
  phases?: CrossClientQueryPhase[]
  createdBy?: string
  hasActiveEvaluations?: boolean
  expand?: ExecutionExpand
}

interface GetCrossClientQueryExecutionsRequest extends BasePageRequest {
  phases?: CrossClientQueryPhase[]
  createdBy?: string
  hasMatches?: boolean
  hasUnactionedMatches?: boolean
  fullyAnalyzed?: boolean
  expand?: ExecutionExpand
}

interface GetCrossClientQueryExecutionRequest extends BaseRequest {
  executionId: string
  expand?: ExecutionExpand
}

interface GetCrossClientResultBucketsRequest extends BaseRequest {
  executionId: string
}

interface GetCrossClientEvaluationResultsRequest extends BaseRequest {
  executionId: string
  evaluationId: string
  page: number
  size: number
}

interface GetCrossClientEvaluationsRequest extends BaseRequest {
  executionId: string
  statuses?: CrossClientQueryEvaluationStatus[]
  types?: CrossClientQueryEvaluationType[]
  expand?: 'progress'[]
}

interface ReportCrossClientViewRequest extends BaseRequest {
  executionId: string
}

interface DeleteCrossClientQueryExecutionRequest extends BaseRequest {
  executionId: string
}
interface GetAllCrossClientActionsRequest extends BaseRequest {
  executionId: string
  updatedAfter?: string | null
}

interface ChangeCrossClientQueryResultMatchRequest extends BaseRequest {
  executionId: string
  resultId: string
  match: typeof CrossClientQueryResultMatches.MATCH | typeof CrossClientQueryResultMatches.NO_MATCH
}

interface UpdateCrossClientIntegrationEntitiesRequest extends BaseRequest {
  executionId: string
  connector: string
  relatedToType: string
  sourcePlatform: string
  sourceObject: string
  entityType: string
  entity: Record<string, unknown>
  targets: { externalId: string; clientId: string }[]
}

interface ScheduleCrossClientMeetingsRequest extends BaseRequest {
  executionId: string
  email: {
    eventTypeId: string
    location?: LocationRef | null
  }
  targets: {
    clientId: string
    hostUserId: string
  }[]
}

export const crossClientApi = createApi({
  reducerPath: 'crossClientApi',
  baseQuery: baseQueryWithTokenRefresh(_AI_MANAGEMENT_BASE_URL),
  tagTypes: ['CrossClientQueryExecution', 'CrossClientQueryResult'],
  endpoints: (builder) => ({
    getAllCrossClientQueryExecutions: builder.query<BaseResponse<CrossClientQueryExecution[]>, GetAllCrossClientQueryExecutionsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientQueryExecution>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions`,
            params: {
              ...(request.phases && { phases: request.phases.join() }),
              ...(request.createdBy && { createdBy: request.createdBy }),
              ...(request.hasActiveEvaluations !== undefined && { hasActiveEvaluations: request.hasActiveEvaluations }),
              ...(request.expand?.length && { expand: request.expand.join() }),
            },
          },
          fetchWithBQ,
        )
      },
    }),
    getCrossClientQueryExecutions: builder.query<BasePageResponse<CrossClientQueryExecution[]>, GetCrossClientQueryExecutionsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.phases && { phases: request.phases.join() }),
            ...(request.createdBy && { createdBy: request.createdBy }),
            ...(request.hasMatches !== undefined && { hasMatches: request.hasMatches }),
            ...(request.hasUnactionedMatches !== undefined && { hasUnactionedMatches: request.hasUnactionedMatches }),
            ...(request.fullyAnalyzed !== undefined && { fullyAnalyzed: request.fullyAnalyzed }),
            ...(request.expand?.length && { expand: request.expand.join() }),
          },
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((execution) => ({ type: 'CrossClientQueryExecution' as const, id: execution.id })),
              { type: 'CrossClientQueryExecution' as const, id: 'LIST' },
            ]
          : [{ type: 'CrossClientQueryExecution' as const, id: 'LIST' }],
    }),
    getCrossClientQueryExecution: builder.query<CrossClientQueryExecution | null, GetCrossClientQueryExecutionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}`,
          params: { ...(request.expand?.length && { expand: request.expand.join() }) },
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: { data: CrossClientQueryExecution } | undefined) => {
        return response?.data ?? null
      },
    }),
    getAllCrossClientQueryExecutionResults: builder.query<BaseResponse<CrossClientQueryResult[]>, GetCrossClientQueryExecutionResultsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientQueryResult>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/results`,
            params: {
              ...(request.updatedAfter && { updatedAfter: request.updatedAfter }),
              ...(request.match?.length ? { match: request.match.join(',') } : {}),
              ...(request.status ? { status: request.status } : {}),
              ...(request.lastResolverStage?.length ? { lastResolverStage: request.lastResolverStage.join(',') } : {}),
              ...(request.clientIds?.length ? { clientIds: request.clientIds.join(',') } : {}),
              ...(request.resultIds?.length ? { resultIds: request.resultIds.join(',') } : {}),
              ...(request.search ? { search: request.search } : {}),
              ...(request.hasActions !== undefined ? { hasActions: request.hasActions } : {}),
              ...(request.orderBy ? { orderBy: request.orderBy } : {}),
            },
          },
          fetchWithBQ,
        )
      },
    }),
    getCrossClientQueryExecutionResults: builder.query<BasePageResponse<CrossClientQueryResult[]>, GetCrossClientQueryExecutionResultsPageRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/results`,
          params: {
            page: request.page,
            size: request.size,
            ...(request.match?.length ? { match: request.match.join(',') } : {}),
            ...(request.status ? { status: request.status } : {}),
            ...(request.lastResolverStage?.length ? { lastResolverStage: request.lastResolverStage.join(',') } : {}),
            ...(request.clientIds?.length ? { clientIds: request.clientIds.join(',') } : {}),
            ...(request.resultIds?.length ? { resultIds: request.resultIds.join(',') } : {}),
            ...(request.search ? { search: request.search } : {}),
            ...(request.hasActions !== undefined ? { hasActions: request.hasActions } : {}),
            ...(request.orderBy ? { orderBy: request.orderBy } : {}),
            ...(request.include?.length ? { include: request.include.join(',') } : {}),
          },
        }
      },
      // Reads by id only: list and Preview reads stay frozen while the panel is open.
      providesTags: (_result, _error, { resultIds }) => (resultIds ?? []).map((id) => ({ type: 'CrossClientQueryResult' as const, id })),
    }),
    getCrossClientResultBuckets: builder.query<CrossClientQueryResultBucketCount[], GetCrossClientResultBucketsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/result-buckets`,
        }
      },
      transformResponse: (response: { data: CrossClientQueryResultBucketCount[] }) => response.data,
    }),
    getCrossClientEvaluationResults: builder.query<BasePageResponse<CrossClientQueryEvaluationResultDetail[]>, GetCrossClientEvaluationResultsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/evaluations/${request.evaluationId}/results`,
        params: { page: request.page, size: request.size },
      }),
    }),
    getCrossClientEvaluations: builder.query<BaseResponse<CrossClientQueryEvaluation[]>, GetCrossClientEvaluationsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientQueryEvaluation>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/evaluations`,
            params: {
              ...(request.statuses?.length ? { statuses: request.statuses.join(',') } : {}),
              ...(request.types?.length ? { types: request.types.join(',') } : {}),
              ...(request.expand?.length ? { expand: request.expand.join(',') } : {}),
            },
          },
          fetchWithBQ,
        )
      },
    }),
    deleteCrossClientQueryExecution: builder.mutation<null, DeleteCrossClientQueryExecutionRequest>({
      query: ({ accountId, executionId }) => ({
        url: `/accounts/${accountId}/cross-client/executions/${executionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { executionId }) => [
        { type: 'CrossClientQueryExecution', id: executionId },
        { type: 'CrossClientQueryExecution', id: 'LIST' },
      ],
    }),
    reportCrossClientView: builder.mutation<void, ReportCrossClientViewRequest>({
      query: ({ accountId, executionId }) => ({
        url: `/accounts/${accountId}/cross-client/executions/${executionId}/report-view`,
        method: 'POST',
      }),
    }),
    getAllCrossClientActions: builder.query<BaseResponse<CrossClientAction[]>, GetAllCrossClientActionsRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<CrossClientAction>(
          {
            url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/actions`,
            params: { ...(request.updatedAfter && { updatedAfter: request.updatedAfter }) },
          },
          fetchWithBQ,
        )
      },
    }),
    updateCrossClientIntegrationEntities: builder.mutation<void, UpdateCrossClientIntegrationEntitiesRequest>({
      query: ({ accountId, ...body }) => ({
        url: `/accounts/${accountId}/cross-client/update-integration-entities`,
        method: 'POST',
        body,
      }),
    }),
    scheduleCrossClientMeetings: builder.mutation<void, ScheduleCrossClientMeetingsRequest>({
      query: ({ accountId, ...body }) => ({
        url: `/accounts/${accountId}/cross-client/schedule-meetings`,
        method: 'POST',
        body,
      }),
    }),
    changeCrossClientQueryResultMatch: builder.mutation<CrossClientQueryResult, ChangeCrossClientQueryResultMatchRequest>({
      query: ({ accountId, executionId, resultId, match }) => ({
        url: `/accounts/${accountId}/cross-client/executions/${executionId}/results/${resultId}/change-match`,
        method: 'POST',
        body: { match },
      }),
      transformResponse: (response: { data: CrossClientQueryResult }) => response.data,
      invalidatesTags: (_result, error, { resultId }) => (error ? [] : [{ type: 'CrossClientQueryResult', id: resultId }]),
    }),
    getCrossClientQueryExecutionProgress: builder.query<CrossClientQueryExecutionProgress | null, GetCrossClientQueryExecutionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/cross-client/executions/${request.executionId}/progress`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: { data: CrossClientQueryExecutionProgressResponse[] }) => {
        if (!response.data?.length) {
          return null
        }
        return {
          executionId: response.data[0].executionId,
          phase: response.data[0].phase,
          finishedAt: response.data[0].finishedAt,
          canceledAt: response.data[0].canceledAt,
          lastUpdatedAt: response.data[0].lastUpdatedAt,
          statuses: response.data.reduce(
            (record, response) => {
              record[response.status] = response.resultCount
              return record
            },
            {} as Record<CrossClientQueryResultStatus, number>,
          ),
        }
      },
    }),
    getAllPredefinedCrossClientQueries: builder.query<BaseResponse<PredefinedCrossClientQuery[]>, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<PredefinedCrossClientQuery>({ url: `/cross-client/predefined-queries` }, fetchWithBQ)
      },
    }),
  }),
})

export const {
  useGetAllPredefinedCrossClientQueriesQuery,
  useGetCrossClientQueryExecutionsQuery,
  useGetCrossClientQueryExecutionQuery,
  useLazyGetCrossClientQueryExecutionQuery,
  useGetAllCrossClientQueryExecutionsQuery,
  useLazyGetAllCrossClientQueryExecutionsQuery,
  useLazyGetAllCrossClientQueryExecutionResultsQuery,
  useLazyGetCrossClientQueryExecutionResultsQuery,
  useGetCrossClientQueryExecutionResultsQuery,
  useLazyGetCrossClientResultBucketsQuery,
  useLazyGetCrossClientEvaluationsQuery,
  useLazyGetCrossClientEvaluationResultsQuery,
  useLazyGetCrossClientQueryExecutionProgressQuery,
  useDeleteCrossClientQueryExecutionMutation,
  useLazyGetAllCrossClientActionsQuery,
  useUpdateCrossClientIntegrationEntitiesMutation,
  useScheduleCrossClientMeetingsMutation,
  useChangeCrossClientQueryResultMatchMutation,
  useReportCrossClientViewMutation,
} = crossClientApi
